import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Checkbox, ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { ROLE_LABELS, useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/settings/users')({ component: ManageUsers });

interface Membership {
  userId: string;
  email: string;
  name: string;
  role: keyof typeof ROLE_LABELS;
  closeRights: boolean;
  hasSignedIn: boolean;
  createdAt: string;
}

const STAFF = new Set(['outss_admin', 'outss_accountant']);
const ROLE_HELP: Record<string, string> = {
  client_owner: 'Sees everything in the books; approvals and uploads are coming soon.',
  client_viewer: 'Read-only access to reports, sales and expenses.',
  outss_accountant: 'Accounting staff: keeps the books, posts and reverses entries.',
  outss_admin: 'Accounting staff with access to every client and to admin roles.',
};

function useMemberships() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['memberships', orgId],
    queryFn: () => api<Membership[]>(`/orgs/${orgId}/memberships`),
    enabled: Boolean(orgId),
  });
}

/** Screen G2 (document 06): the org's users, client users first, plus assigned Outss staff. */
function ManageUsers() {
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const isAdmin = me.data?.roles.includes('outss_admin') ?? false;
  const list = useMemberships();
  const [editing, setEditing] = useState<{ open: boolean; member: Membership | null }>({
    open: false,
    member: null,
  });
  const [removing, setRemoving] = useState<Membership | null>(null);

  const clients = (list.data ?? []).filter((m) => !STAFF.has(m.role));
  const staff = (list.data ?? []).filter((m) => STAFF.has(m.role));

  const table = (rows: Membership[], heading: string) => (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-4 py-2 text-sm font-semibold">{heading}</div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">No users yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Close rights</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="w-[150px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => {
              const self = m.userId === me.data?.userId;
              const locked = m.role === 'outss_admin' && !isAdmin;
              return (
                <TableRow key={m.userId}>
                  <TableCell className="font-medium">
                    {m.name}
                    {self ? (
                      <Badge variant="secondary" className="ml-2">
                        You
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>{ROLE_LABELS[m.role] ?? m.role}</TableCell>
                  <TableCell>{STAFF.has(m.role) ? (m.closeRights ? 'Yes' : 'No') : '—'}</TableCell>
                  <TableCell>
                    {m.hasSignedIn ? (
                      <Badge variant="outline">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Invited</Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(m.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && !locked ? (
                      <div className="inline-flex">
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => setEditing({ open: true, member: m })}
                        >
                          Edit
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`More actions for ${m.name}`}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={self}
                              onSelect={() => setRemoving(m)}
                              title={self ? 'You cannot remove your own access' : undefined}
                            >
                              Remove user
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Manage users"
        description="Who can open this client's books."
        actions={
          canEdit ? (
            <Button onClick={() => setEditing({ open: true, member: null })}>Add user</Button>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load users" hint={list.error.message} />
      ) : (
        <div className="flex flex-col gap-4">
          {table(clients, 'Client users')}
          {table(staff, 'Accounting staff')}
          <p className="text-xs text-muted-foreground">
            Invited users get access when they first sign in with this email address. Sign-in emails
            are coming soon.
          </p>
        </div>
      )}
      <UserDrawer
        open={editing.open}
        member={editing.member}
        isAdmin={isAdmin}
        onOpenChange={(open) => setEditing((e) => ({ ...e, open }))}
      />
      <RemoveDialog member={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

function UserDrawer({
  open,
  member,
  isAdmin,
  onOpenChange,
}: {
  open: boolean;
  member: Membership | null;
  isAdmin: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    name: '',
    role: 'client_viewer',
    closeRights: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      member
        ? {
            email: member.email,
            name: member.name,
            role: member.role,
            closeRights: member.closeRights,
          }
        : { email: '', name: '', role: 'client_viewer', closeRights: false },
    );
  }, [open, member]);

  const save = useMutation({
    mutationFn: () =>
      api<Membership[]>(`/orgs/${orgId}/memberships`, {
        method: 'PUT',
        body: JSON.stringify({
          email: form.email.trim(),
          ...(form.name.trim() ? { name: form.name.trim() } : {}),
          role: form.role,
          closeRights: STAFF.has(form.role) ? form.closeRights : false,
        }),
      }),
    onSuccess: (rows) => {
      qc.setQueryData(['memberships', orgId], rows);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(member ? `${form.name || form.email} updated.` : `${form.email} invited.`);
      onOpenChange(false);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      next['email'] = 'Enter a valid email address.';
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const roles = [
    'client_owner',
    'client_viewer',
    'outss_accountant',
    ...(isAdmin ? ['outss_admin'] : []),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{member ? 'Edit user' : 'Add user'}</SheetTitle>
            <SheetDescription>
              {member ? member.email : 'Invite someone by email and choose what they can do.'}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{save.error?.message}</ErrorBanner>
            <Field label="Email" error={errors['email']}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={form.email}
                  disabled={Boolean(member)}
                  aria-invalid={Boolean(errors['email'])}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  autoFocus={!member}
                />
              )}
            </Field>
            <Field label="Name" hint={member ? undefined : 'Optional; defaults to the email.'}>
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  disabled={Boolean(member)}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </Field>
            <Field label="Role" hint={ROLE_HELP[form.role]}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {STAFF.has(form.role) ? (
              <Checkbox
                label="Can lock and unlock periods (close rights)"
                checked={form.closeRights}
                onChange={(e) => setForm((f) => ({ ...f, closeRights: e.target.checked }))}
              />
            ) : null}
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : member ? 'Save' : 'Send invite'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function RemoveDialog({ member, onClose }: { member: Membership | null; onClose: () => void }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (x: Membership) =>
      api<Membership[]>(`/orgs/${orgId}/memberships/${x.userId}`, { method: 'DELETE' }),
    onSuccess: (rows, x) => {
      qc.setQueryData(['memberships', orgId], rows);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(`${x.name} no longer has access.`);
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(member)}
      onOpenChange={(open) => {
        if (!open) {
          m.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {member?.name}?</DialogTitle>
          <DialogDescription>
            They lose access to this client&apos;s books straight away. Everything they did stays in
            the audit log. You can add them again later.
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <p role="alert" className="text-sm text-destructive">
            {m.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={m.isPending}
            onClick={() => member && m.mutate(member)}
          >
            Remove user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
